import { createListenerMiddleware, ListenerEffectAPI } from '@reduxjs/toolkit'
import { ActivityPathId } from '@/api/models/aggregate'
import { objectiveWorkflowApi } from '@/store/api/objectiveWorkflowApi'
import { AggregateState } from '@/store/slices/aggregateSlice'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { getActivityPartConstraintLifetime } from './helpers/getActivityPartConstraintLifetime'
import { getLifeTimeTimeoutAccordingConstraints } from './helpers/getLifeTimeTimeoutAccordingConstraints'

export const refetchActivityAccordingConstraintMiddleware = createListenerMiddleware()

const timeoutsMap = new Map<string, NodeJS.Timeout>()

refetchActivityAccordingConstraintMiddleware.startListening({
  matcher: objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowAggregate.matchFulfilled,
  effect: async (action, listenerApi: ListenerEffectAPI<any, any>) => {
    const dispatch = listenerApi.dispatch
    const aggregate: AggregateState = listenerApi.getState().aggregate
    const activityWithAggregates = aggregate.activitiesWithAggregates
    const activityPathId = action.meta.arg.originalArgs.activityPathId || ''
    const objectiveWorkflowId = action.meta.arg.originalArgs.objectiveWorkflowId || ''

    const activityPart = findActivityPartByActivityPathId(activityPathId, activityWithAggregates)
    if (!activityPart) return

    const lifetime = getActivityPartConstraintLifetime(activityPart)
    if (lifetime === null) return

    if (timeoutsMap.has(activityPathId)) {
      clearTimeout(timeoutsMap.get(activityPathId)!)
      timeoutsMap.delete(activityPathId)
    }

    const timeout = setTimeout(() => {
      dispatch(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowAggregate.initiate(
          { objectiveWorkflowId, activityPathId },
          { forceRefetch: 1 },
        ),
      )
    }, lifetime)

    timeoutsMap.set(activityPathId, timeout)
  },
})

refetchActivityAccordingConstraintMiddleware.startListening({
  matcher: objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.matchFulfilled,
  effect: async (action, listenerApi: ListenerEffectAPI<any, any>) => {
    const dispatch = listenerApi.dispatch
    const aggregate: AggregateState = listenerApi.getState().aggregate
    const activityWithAggregates = aggregate.activitiesWithAggregates
    const activityPathId = action.meta.arg.originalArgs.activityPathId || ''
    const objectiveWorkflowId = action.meta.arg.originalArgs.objectiveWorkflowId || ''

    const activityPart = activityPathId
      ? findActivityPartByActivityPathId(activityPathId, activityWithAggregates)
      : ({
          childActivity: activityWithAggregates,
          activityPathId: '' as ActivityPathId,
        } as FrontActivityPart<FrontActivityWithAggregate>)

    if (!activityPart) return

    const lifetimeOfParentActivityParts = getActivityPartConstraintLifetime(activityPart)
    const lifetimeOfChildActivityParts = getLifeTimeTimeoutAccordingConstraints(
      activityPart.childActivity?.children || [],
    )

    if (lifetimeOfParentActivityParts === null && lifetimeOfChildActivityParts === null) return

    const lifetime = Math.max(
      lifetimeOfParentActivityParts !== null ? lifetimeOfParentActivityParts : -Infinity,
      lifetimeOfChildActivityParts !== null ? lifetimeOfChildActivityParts : -Infinity,
    )

    if (timeoutsMap.has(activityPathId)) {
      clearTimeout(timeoutsMap.get(activityPathId)!)
      timeoutsMap.delete(activityPathId)
    }

    const timeout = setTimeout(() => {
      dispatch(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.initiate(
          { objectiveWorkflowId, activityPathId },
          { forceRefetch: 1 },
        ),
      )
    }, lifetime)

    timeoutsMap.set(activityPathId, timeout)
  },
})

const findActivityPartByActivityPathId = (
  activityPathId: ActivityPathId | undefined,
  activitiesWithAggregates: FrontActivityWithAggregate | null,
) => {
  if (!activitiesWithAggregates || !activityPathId) {
    return null
  }

  const stack = [...(activitiesWithAggregates.children || [])]

  while (stack.length > 0) {
    const activityPart = stack.pop()!

    if (activityPart.activityPathId === activityPathId) {
      return activityPart
    }

    stack.push(...activityPart.childActivity.children)
  }

  return null
}
